// Korpus-Generator: kompletter Schrankkorpus aus Parametern — Seiten, Boden,
// Deckel, optional Rückwand (aufgesetzt, 8 mm) und verteilte Fachböden.
// Alle Teile sind an die linke Seite geheftet (Korpus bewegt sich als Ganzes).
// Korpus steht zentriert um x/z = 0, Unterkante auf dem Boden. Engine-frei.
import { newPart, generateClientId } from "./document"
import { DEFAULT_PART_TYPE } from "./part_types"

export const CABINET_DEFAULTS = {
  width: 800, height: 2000, depth: 600, thickness: 19, shelves: 2, back: true
}

// Generierte Ankerposition (Seite links) für gegebene Parameter — Gegenstück
// zum Aufbau in buildCabinet, für den Positions-Delta beim Bearbeiten.
export function cabinetAnchorPos({ width, height, thickness }) {
  const roundPos = (v) => Math.sign(v) * Math.round(Math.abs(v))
  return { x: roundPos(-(width - thickness) / 2), y: roundPos(height / 2), z: 0 }
}

// Positionen sind Ganzzahl-mm (DB-Konvention). Bei ungerader Materialstärke
// entstehen halbe Millimeter — weg von Null runden, damit links/rechts
// spiegelsymmetrisch bleiben (max. 0,5 mm Versatz, Maße bleiben exakt).
const roundPos = (v) => Math.sign(v) * Math.round(Math.abs(v))

export function buildCabinet(params = {}) {
  const { width, height, depth, thickness, shelves, back, materialId = null, groupId = generateClientId() } =
    { ...CABINET_DEFAULTS, ...params }
  const s = thickness
  if (width < 3 * s || height < 3 * s || depth < 50) return null

  const parts = []
  const withIntegerPos = (attrs) => ({
    ...attrs,
    pos_x: roundPos(attrs.pos_x), pos_y: roundPos(attrs.pos_y), pos_z: roundPos(attrs.pos_z)
  })
  // Der Anker trägt die Gruppen-Id und die Parameter — Grundlage für
  // nachträgliches Bearbeiten (Dialog vorbefüllen, Teile ersetzen).
  const left = newPart(withIntegerPos({
    name: "Seite links", material_id: materialId,
    length_mm: height, width_mm: depth, thickness_mm: s,
    rot_z: 90,
    pos_x: -(width - s) / 2, pos_y: height / 2, pos_z: 0,
    meta: {
      cabinet_id: groupId, role: "seite-links", part_type: DEFAULT_PART_TYPE,
      cabinet: { width, height, depth, thickness, shelves, back }
    }
  }))
  parts.push(left)

  // `role` ist der stabile Schlüssel eines Teils in der Gruppe: bei gleichen
  // Parametern deterministisch, über Parameteränderungen hinweg gleichbleibend.
  // Daran hängt beim Bearbeiten die Übernahme der Handarbeit (generated_groups.js).
  // `part_type` ist ohne eigene Angabe Korpus — nur die Rückwand schert aus.
  const attach = (role, attrs) => {
    parts.push(newPart(withIntegerPos({
      material_id: materialId, ...attrs,
      meta: {
        part_type: DEFAULT_PART_TYPE, ...(attrs.meta ?? {}),
        role, attached_to: left.client_id, cabinet_id: groupId
      }
    })))
  }

  attach("seite-rechts", {
    name: "Seite rechts",
    length_mm: height, width_mm: depth, thickness_mm: s,
    rot_z: 90,
    pos_x: (width - s) / 2, pos_y: height / 2, pos_z: 0
  })
  attach("boden", {
    name: "Boden",
    length_mm: width - 2 * s, width_mm: depth, thickness_mm: s,
    pos_x: 0, pos_y: s / 2, pos_z: 0
  })
  attach("deckel", {
    name: "Deckel",
    length_mm: width - 2 * s, width_mm: depth, thickness_mm: s,
    pos_x: 0, pos_y: height - s / 2, pos_z: 0
  })

  // Fachböden gleichmäßig zwischen Boden-Oberkante und Deckel-Unterkante,
  // 20 mm weniger tief (Luft für eine spätere Rückwand/Front).
  // Rollen von unten nach oben durchnummeriert: Wird die Anzahl geändert,
  // behalten die verbleibenden Fachböden ihre Rolle (und ihre Bearbeitungen).
  const free = height - 2 * s - shelves * s
  if (shelves > 0 && free > 0) {
    const gap = free / (shelves + 1)
    for (let i = 0; i < shelves; i++) {
      attach(`fachboden-${i + 1}`, {
        name: "Fachboden",
        length_mm: width - 2 * s, width_mm: Math.max(50, depth - 20), thickness_mm: s,
        pos_x: 0, pos_y: Math.round(s + gap * (i + 1) + s * i + s / 2), pos_z: 0
      })
    }
  }

  if (back) {
    attach("rueckwand", {
      name: "Rückwand",
      length_mm: width, width_mm: height, thickness_mm: 8,
      rot_x: 90,
      pos_x: 0, pos_y: height / 2, pos_z: depth / 2 + 4,
      meta: { part_type: "rueckwand" }
    })
  }

  return parts
}
